import { EngagementDto, UserRolesEnum } from '@families-link/shared';
import { FC, useCallback, useMemo } from 'react';
import { CoreTableActionsCell, CellAction, CoreActionButton } from '../../../_core/components/_ui/core-table';
import { EngagementsTableHandlers } from './engagements-table.component';
import { DeleteIcon, EditIcon, EyeIcon, SendSmsIcon, ShareIcon } from '../../../_core/components/icons';
import { ArchiveIcon } from '../../../_core/constants/icons.constants';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCurrentAccessTokenSelector } from '../../../auth';

export type AllowedEngagementActions = 'email' | 'sms' | 'edit' | 'delete' | 'view' | 'archive';
export interface EngagementsTableActionsProps extends EngagementsTableHandlers {
	engagement: EngagementDto;
	allowedEngagementActions: AllowedEngagementActions[];
	isLinguistLink?: boolean;
	isAdminRoute?: boolean;
}

export const EngagementsTableActions: FC<EngagementsTableActionsProps> = (props) => {
	const {
		engagement,
		allowedEngagementActions,
		isAdminRoute,
		onDelete,
		onEdit,
		onView,
		onArchive,
		onSendEmail,
		onSendSMS,
		onUnarchive,
		isLinguistLink,
	} = props;
	const [t] = useTranslation();

	const handleDeleteEngagement = useCallback(() => {
		if (!engagement) {
			return;
		}

		onDelete?.(engagement);
	}, [onDelete, engagement]);

	const handleEditEngagement = useCallback(() => {
		if (!engagement) {
			return;
		}

		onEdit?.(engagement);
	}, [onEdit, engagement]);

	const handleViewEngagement = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (!engagement) {
				return;
			}

			onView?.(e, engagement);
		},
		[onView, engagement]
	);
	const handleSendEmailEngagement = useCallback(() => {
		if (!engagement) {
			return;
		}

		onSendEmail?.(engagement);
	}, [onSendEmail, engagement]);

	const handleSendSMSEngagement = useCallback(() => {
		if (!engagement) {
			return;
		}

		onSendSMS?.(engagement);
	}, [onSendSMS, engagement]);

	const handleArchiveEngagement = useCallback(() => {
		if (!engagement) {
			return;
		}

		if (engagement.archivedAt) {
			onUnarchive?.(engagement);
		} else {
			onArchive?.(engagement);
		}
	}, [onArchive, onUnarchive, engagement]);

	const currentToken = useCurrentAccessTokenSelector();
	const isSuperAdmin = currentToken?.payload.sur === UserRolesEnum.SuperAdmin;

	const isLLEngagement = engagement.isFromSecondary
	const targetLink = 
		(isAdminRoute ? '/admin' : '') + 
		(isLLEngagement 
			? `/engagements/${engagement._id}/item/${engagement.projectItemId}?isLinguistLink=true`
			: `/engagements/${engagement._id}`)
	
	const actions = useMemo<CellAction[]>(
		() => [
			{
				id: 'view',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.full-details'),
				hidden: !allowedEngagementActions.includes('view'),

				props: {
					children: (
						<Link to={`${targetLink}${isSuperAdmin && !isAdminRoute ? `?districtId=${engagement.district?._id}` : ''}`} target="_blank" rel="noopener noreferrer">
							<EyeIcon sx={{ fontSize: 30 }} color="primary" />
						</Link>
					),
					onClick: handleViewEngagement,
				},
			},
			{
				id: 'more',
				type: 'menu',
				hidden:
					isLinguistLink ||
					!allowedEngagementActions.some(
						(el: AllowedEngagementActions) =>
							el === 'edit' || el === 'delete' || el === 'archive' || el === 'sms' || el === 'email'
					),
				props: {
					options: [
						{
							id: 'email',
							label: 'Share by email',
							icon: <ShareIcon color="primary" />,
							onClick: handleSendEmailEngagement,
							disabled: true,
							hidden: !allowedEngagementActions.includes('email'),
						},
						{
							id: 'sms',
							label: 'Send SMS',
							icon: <SendSmsIcon color="primary" />,
							onClick: handleSendSMSEngagement,
							disabled: true,
							hidden: !allowedEngagementActions.includes('sms'),
						},
						{
							id: 'archive',
							label: engagement.archivedAt ? 'Unarchive' : 'Archive',
							icon: <ArchiveIcon color="primary" />,
							onClick: handleArchiveEngagement,
							hidden: !allowedEngagementActions.includes('archive'),
						},
						{
							id: 'edit',
							label: 'Edit',
							icon: <EditIcon color="primary" />,
							onClick: handleEditEngagement,
							hidden: !allowedEngagementActions.includes('edit'),
						},
						{
							id: 'delete',
							label: 'Delete',
							icon: <DeleteIcon color="primary" />,
							onClick: handleDeleteEngagement,
							hidden: !allowedEngagementActions.includes('delete'),
						},
					],
				},
			},
		],
		[
			allowedEngagementActions,
			isLinguistLink,
			handleViewEngagement,
			handleEditEngagement,
			handleDeleteEngagement,
			handleSendEmailEngagement,
			handleSendSMSEngagement,
		]
	);

	return <CoreTableActionsCell actions={actions} />;
};
